import { useState, useEffect, useCallback } from "react";

// Tạo storage key dựa trên examId và userId
const getStorageKey = (examId, assessmentId) => {
  return `exam_answers_${examId}_${assessmentId}`;
};

// Lưu đáp án vào sessionStorage với merge logic
const saveAnswersToStorage = (examId, assessmentId, answers) => {
  try {
    const storageKey = getStorageKey(examId, assessmentId);
    sessionStorage.setItem(storageKey, JSON.stringify(answers));
  } catch (error) {
    console.error("Error saving answers to sessionStorage:", error);
  }
};

// Lấy đáp án từ sessionStorage
const getAnswersFromStorage = (examId, assessmentId) => {
  try {
    const storageKey = getStorageKey(examId, assessmentId);
    const savedAnswers = sessionStorage.getItem(storageKey);
    if (savedAnswers) {
      return JSON.parse(savedAnswers);
    }
    return null;
  } catch (error) {
    console.error("Error getting answers from sessionStorage:", error);
    return null;
  }
};

/**
 * Custom hook để quản lý answers cho exam
 * Centralized logic để đồng bộ giữa ExamTestPage và MathRenderer
 */
export const useExamAnswers = (examId, assessmentId, initialAnswers = {}) => {
  const [answers, setAnswers] = useState(initialAnswers);

  // Load answers từ sessionStorage khi hook được khởi tạo
  useEffect(() => {
    const savedAnswers = getAnswersFromStorage(examId, assessmentId);
    if (savedAnswers) {
      setAnswers(savedAnswers);
    } else if (Object.keys(initialAnswers).length > 0) {
      // Nếu không có saved answers nhưng có initialAnswers, sử dụng initialAnswers
      setAnswers(initialAnswers);
    }
  }, [examId, assessmentId]);

  // Auto-save answers mỗi khi thay đổi
  useEffect(() => {
    if (Object.keys(answers).length > 0) {
      saveAnswersToStorage(examId, assessmentId, answers);
    }
  }, [answers, examId, assessmentId]);

  // Function để update answer cho một câu hỏi cụ thể
  const updateAnswer = useCallback((questionId, newAnswer) => {
    setAnswers((prev) => {
      const updated = {
        ...prev,
        [questionId]: newAnswer,
      };
      return updated;
    });
  }, []);

  // Function để update partial answer (cho TLN_M, DS, KT)
  const updatePartialAnswer = useCallback((questionId, partialData) => {
    setAnswers((prev) => {
      const currentAnswer = prev[questionId] || {};
      const updated = {
        ...prev,
        [questionId]: {
          ...currentAnswer,
          ...partialData,
        },
      };
      return updated;
    });
  }, []);

  // Function để toggle item trong array (cho MA type)
  const toggleArrayAnswer = useCallback((questionId, item) => {
    setAnswers((prev) => {
      const currentAnswers = prev[questionId] || [];
      const newAnswers = currentAnswers.includes(item)
        ? currentAnswers.filter((a) => a !== item)
        : [...currentAnswers, item];

      const updated = {
        ...prev,
        [questionId]: newAnswers,
      };
      return updated;
    });
  }, []);

  // Function để clear all answers (cho submission)
  const clearAnswers = useCallback(() => {
    const storageKey = getStorageKey(examId, assessmentId);
    sessionStorage.removeItem(storageKey);
    setAnswers({});
  }, [examId, assessmentId]);

  // Function để force refresh answers từ storage
  const refreshAnswers = useCallback(() => {
    const savedAnswers = getAnswersFromStorage(examId, assessmentId);
    if (savedAnswers) {
      setAnswers(savedAnswers);
    }
  }, [examId, assessmentId]);

  return {
    answers,
    updateAnswer,
    updatePartialAnswer,
    toggleArrayAnswer,
    clearAnswers,
    refreshAnswers,
    // Utility functions
    getAnswerForQuestion: (questionId) => answers[questionId],
    hasAnswerForQuestion: (questionId) =>
      questionId in answers &&
      answers[questionId] !== null &&
      answers[questionId] !== undefined,
  };
};

export default useExamAnswers;
